import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { WorkCalendar } from "../components/planning/WorkCalendar";

export default function Settings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const { data, reload } = useFetch("/app-settings", [], isSuper);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setVals(data.values); }, [data]);
  if (!isSuper) return <Navigate to="/" replace />;
  const schema = data?.schema || [];
  const groups = [...new Set(schema.map((p) => p.group))];
  const dirty = schema.some((p) => String(vals[p.key]) !== String(data?.values?.[p.key]));
  const save = async () => {
    setBusy(true);
    try { await api.put("/app-settings", { values: vals }); toast.success("Pengaturan disimpan"); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const resetDefaults = () => setVals(Object.fromEntries(schema.map((p) => [p.key, p.default])));
  return (
    <div>
      <PageHeader title={t("settings")} subtitle="Parameter global aplikasi · hanya Super Admin" actions={<div className="flex gap-2">
        <Button data-testid="settings-reset-button" variant="outline" onClick={resetDefaults}><RotateCcw className="h-4 w-4 mr-1" />Nilai bawaan</Button>
        <Button data-testid="settings-save-button" className="bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-dark)]" disabled={!dirty || busy} onClick={save}><Save className="h-4 w-4 mr-1" />{t("save")}</Button>
      </div>} />
      <div className="grid gap-4 lg:grid-cols-2" data-testid="settings-groups">
        {groups.map((g) => (
          <div key={g} className="surface p-5" data-testid={`settings-group-${g}`}>
            <p className="font-heading font-bold text-sm mb-3">{g}</p>
            <div className="space-y-4">
              {schema.filter((p) => p.group === g).map((p) => (
                <div key={p.key} className="grid grid-cols-[1fr_7rem] gap-3 items-start">
                  <div><p className="text-sm font-medium">{p.label}</p><p className="text-xs text-slate-500">{p.help} <span className="text-slate-400">(bawaan {p.default})</span></p></div>
                  <Input data-testid={`setting-${p.key}`} type="number" min={p.min} max={p.max} value={vals[p.key] ?? ""} onChange={(e) => setVals({ ...vals, [p.key]: e.target.value })} />
                </div>
              ))}
            </div>
          </div>
        ))}
        <WorkCalendar />
      </div>
    </div>
  );
}
